import React, { useState, useEffect, useRef } from 'react';
import { Card, Switch, Button, Table, Tag, Space, message, Alert, Statistic, Row, Col, Modal, Divider, Typography, Spin, Select, Input, InputNumber, Form, Tabs, Progress, Badge, Timeline, Tooltip } from 'antd';
import { SafetyCertificateOutlined, SyncOutlined, ApiOutlined, DollarOutlined, ExclamationCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, PlayCircleOutlined, DatabaseOutlined, WarningOutlined, DeleteOutlined, SettingOutlined, ClockCircleOutlined, ThunderboltOutlined, TeamOutlined, HistoryOutlined, ExperimentOutlined, LoadingOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';

const { Title, Text } = Typography;


const SystemMaintenance = () => {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState({
    api_enabled: true,
    user_balance_count: 0,
    affiliate_count: 0
  });
  const [comparison, setComparison] = useState({
    user_balances: [],
    affiliate_balances: []
  });
  const [showComparison, setShowComparison] = useState(false);

  // Redis Stream 状态
  const [streamStatus, setStreamStatus] = useState(null);
  const [streamLoading, setStreamLoading] = useState(false);
  const [streamGroup, setStreamGroup] = useState('balance-consumers');
  const [processingStream, setProcessingStream] = useState(false);

  const currentGroupPending = streamStatus?.groups?.find(g => g.name === streamGroup)?.pending || 0;

  const lastStepRef = useRef(null); // 用于滚动到最后一个测试步骤

  // 消费者配置
  const [consumerConfig, setConsumerConfig] = useState({
    balance_consumers: 3,
    commission_consumers: 3,
    stream_keep_count: 10000,
    trim_interval_minutes: 1440,
    trim_trigger_count: 0,
    reconciliation_interval_minutes: 5
  });
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configForm] = Form.useForm();

  // 上线测试相关状态
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testSteps, setTestSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(null); // 当前正在执行的步骤
  const [testProgress, setTestProgress] = useState(0);
  const [testModel, setTestModel] = useState('MiniMax-M2.7');
  const testContainerRef = useRef(null); // 用于自动滚动

  useEffect(() => {
    fetchStatus();
    fetchStreamStatus();
    fetchConsumerConfig();
  }, []);

  const fetchConsumerConfig = async () => {
    try {
      const response = await adminAPI.getConsumerConfig();
      if (response.code === 0) {
        setConsumerConfig(prev => ({
          ...prev,
          ...response.data
        }));
      }
    } catch (error) {
      console.error('获取消费者配置失败:', error);
    }
  };

  const handleSaveConsumerConfig = async (values) => {
    setSavingConfig(true);
    try {
      const response = await adminAPI.updateConsumerConfig(values);
      if (response.code === 0) {
        message.success(response.data?.hot_reload ? '配置已保存并实时生效' : '配置已保存');
        setConsumerConfig({
          ...values,
          hot_reload: response.data?.hot_reload
        });
        setConfigModalVisible(false);
      } else {
        message.error(response.message || '保存失败');
      }
    } catch (error) {
      message.error('保存失败: ' + (error.message || '网络错误'));
    } finally {
      setSavingConfig(false);
    }
  };

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getSystemStatus();
      if (response.code === 0) {
        setStatus(response.data);
      }
    } catch (error) {
      message.error('获取系统状态失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchChange = async (checked) => {
    try {
      const response = await adminAPI.setSystemSwitch(checked);
      if (response.code === 0) {
        message.success(response.data.message);
        fetchStatus();
      }
    } catch (error) {
      message.error('设置失败');
      fetchStatus();
    }
  };

  const handleSync = async () => {
    Modal.confirm({
      title: '确认同步',
      icon: <ExclamationCircleOutlined />,
      content: '确定要将所有Redis余额同步到MySQL吗？这会覆盖数据库中的余额数据。',
      okText: '确认同步',
      cancelText: '取消',
      onOk: async () => {
        setSyncing(true);
        try {
          const response = await adminAPI.syncAllBalances();
          if (response.code === 0) {
            message.success(response.data.message);
          }
        } catch (error) {
          message.error('同步失败');
        } finally {
          setSyncing(false);
        }
      }
    });
  };

  const handleCompare = async () => {
    try {
      const response = await adminAPI.getBalanceComparison();
      if (response.code === 0) {
        setComparison(response.data);
        setShowComparison(true);
      }
    } catch (error) {
      message.error('获取对比数据失败');
    }
  };

  const fetchStreamStatus = async () => {
    setStreamLoading(true);
    try {
      const response = await adminAPI.getStreamStatus();
      if (response.code === 0) {
        setStreamStatus(response.data);
        if (response.data?.config) {
          setConsumerConfig(prev => ({
            ...prev,
            balance_consumers: response.data.config.balance_consumers ?? prev.balance_consumers,
            commission_consumers: response.data.config.commission_consumers ?? prev.commission_consumers
          }));
        }
      }
    } catch (error) {
      console.error('获取Stream状态失败:', error);
    } finally {
      setStreamLoading(false);
    }
  };

  const handleRetryPendingMessages = async () => {
    if (!streamStatus?.total_pending || streamStatus.total_pending === 0) {
      message.info('当前没有待处理的 pending 消息');
      return;
    }

    Modal.confirm({
      title: '确认重试处理',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>确定要重试处理所有 pending 消息吗？</p>
          <p>当前 pending 消息数：<strong>{streamStatus.total_pending}</strong></p>
        </div>
      ),
      okText: '确认重试',
      cancelText: '取消',
      onOk: async () => {
        setProcessingStream(true);
        try {
          const response = await adminAPI.retryPendingMessages(streamGroup);
          if (response.code === 0) {
            message.success(`已处理 ${response.data.processed} 条消息`);
            fetchStreamStatus();
          } else {
            message.error(response.message || '重试失败');
          }
        } catch (error) {
          message.error('重试处理失败');
        } finally {
          setProcessingStream(false);
        }
      }
    });
  };

  const handleRetryAllPending = async () => {
    if (!streamStatus?.total_pending || streamStatus.total_pending === 0) {
      message.info('当前没有待处理的 pending 消息');
      return;
    }

    Modal.confirm({
      title: '一键重试处理所有 pending 消息',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>确定要处理所有消费者组的 pending 消息吗？</p>
          <p>当前 pending 消息总数：<strong>{streamStatus.total_pending}</strong></p>
        </div>
      ),
      okText: '确认处理',
      cancelText: '取消',
      onOk: async () => {
        setProcessingStream(true);
        try {
          const response = await adminAPI.retryAllPendingMessages();
          if (response.code === 0) {
            message.success(`已处理 ${response.data.processed} 条消息`);
            fetchStreamStatus();
          } else {
            message.error(response.message || '处理失败');
          }
        } catch (error) {
          message.error('处理失败');
        } finally {
          setProcessingStream(false);
        }
      }
    });
  };

  const handleCleanPendingMessages = async () => {
    if (currentGroupPending === 0) {
      message.info(`当前选中组没有待处理的 pending 消息`);
      return;
    }

    Modal.confirm({
      title: <span style={{ color: '#ff4d4f' }}><WarningOutlined /> 危险操作</span>,
      content: (
        <Alert
          message="此操作将丢弃 pending 消息，不会同步到 MySQL！"
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      ),
      okText: <span style={{ color: '#f8f8f8' }}>确认清理</span>,
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        setProcessingStream(true);
        try {
          const response = await adminAPI.cleanPendingMessages(streamGroup);
          if (response.code === 0) {
            message.success(`已清理 ${response.data.deleted} 条消息`);
            fetchStreamStatus();
          } else {
            message.error(response.message || '清理失败');
          }
        } catch (error) {
          message.error('清理失败');
        } finally {
          setProcessingStream(false);
        }
      }
    });
  };

  const handleTrimProcessedMessages = async () => {
    let selectedStream = 'balance:stream';
    let keepCount = 10000;

    Modal.confirm({
      title: '清理已处理的历史消息',
      icon: <ExclamationCircleOutlined />,
      width: 500,
      content: (
        <div style={{ marginTop: 16 }}>
          <Alert
            message="此操作将删除已确认处理的历史消息，释放内存"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <div style={{ marginBottom: 16 }}>
            <Text>选择 Stream：</Text>
            <Select
              defaultValue="balance:stream"
              style={{ width: '100%', marginTop: 8 }}
              options={[
                { label: 'balance:stream', value: 'balance:stream' },
                { label: 'commission:stream', value: 'commission:stream' }
              ]}
              onChange={(val) => { selectedStream = val; }}
            />
          </div>
          <div>
            <Text>保留最近消息数：</Text>
            <InputNumber
              defaultValue={10000}
              min={10}
              max={100000}
              step={100}
              style={{ width: '100%', marginTop: 8 }}
              onChange={(val) => { keepCount = val; }}
            />
          </div>
        </div>
      ),
      onOk: async () => {
        setProcessingStream(true);
        try {
          const response = await adminAPI.trimProcessedMessages({
            stream: selectedStream,
            strategy: 'count',
            keep_count: keepCount
          });
          if (response.code === 0) {
            message.success(`已清理历史消息，当前保留 ${response.data?.after_len ?? 0} 条`);
            fetchStreamStatus();
          } else {
            message.error(response.message || '清理失败');
          }
        } catch (error) {
          message.error('清理失败');
        } finally {
          setProcessingStream(false);
        }
      }
    });
  };

  // 自动滚动到最新测试结果（让内容区域滚动到底部）
  useEffect(() => {
    if (testSteps.length > 0) {
      const timer = setTimeout(() => {
        // 滚动 .admin-content 容器到底部
        const contentEl = document.querySelector('.admin-content');
        if (contentEl) {
          contentEl.scrollTop = contentEl.scrollHeight;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [testSteps]);

  const runOnlineTest = async () => {
    setTesting(true);
    setTestResult(null);
    setTestSteps([]);
    setCurrentStep(null);
    setTestProgress(0);

    try {
      message.info('基础流程测试开始执行...');
      const startResponse = await adminAPI.runFullTest(testModel);
      if (startResponse.code !== 0) {
        message.error(startResponse.message || '启动测试失败');
        setTestResult('failed');
        setTesting(false);
        return;
      }

      const { test_id } = startResponse.data;
      const totalSteps = startResponse.data.total_steps || 10;

      const pollProgress = async () => {
        try {
          const progressResponse = await adminAPI.getTestProgress(test_id);
          if (progressResponse.code !== 0) {
            setTimeout(pollProgress, 500);
            return;
          }

          const progress = progressResponse.data;
          if (progress.steps && progress.steps.length > 0) {
            const newSteps = progress.steps.map((s, index) => ({
              name: s.name || `步骤 ${index + 1}`,
              status: s.passed === true ? 'success' : s.passed === false ? 'error' : 'pending',
              detail: s.message || '',
            }));
            setTestSteps(newSteps);
            
            // 计算进度
            const completedSteps = newSteps.filter(s => s.status !== 'pending').length;
            setTestProgress(Math.round((completedSteps / totalSteps) * 100));
            
            // 找到当前正在执行的步骤
            const currentPending = newSteps.find(s => s.status === 'pending');
            if (currentPending) {
              setCurrentStep(currentPending.name);
            }
          }

          if (progress.status === 'completed') {
            setCurrentStep(null);
            setTestProgress(100);
            setTestResult(progress.passed_steps === progress.total_steps ? 'passed' : 'failed');
            setTesting(false);
            message.success(progress.passed_steps === progress.total_steps ? '基础流程测试全部通过' : '基础流程测试部分未通过');
            return;
          }

          setTimeout(pollProgress, 300);
        } catch (error) {
          setTimeout(pollProgress, 500);
        }
      };

      pollProgress();
    } catch (error) {
      message.error('测试执行失败');
      setTestResult('failed');
      setTesting(false);
    }
  };

  const clearCache = async () => {
    try {
      await adminAPI.clearCache();
      message.success('缓存清理成功');
    } catch (e) {
      message.error('清理失败: ' + (e.message || '未知错误'));
    }
  };

  const runMerchantCommissionTest = async () => {
    setTesting(true);
    setTestResult(null);
    setTestSteps([]);
    setCurrentStep(null);
    setTestProgress(0);

    try {
      message.info('商户佣金测试开始执行...');
      const startResponse = await adminAPI.runMerchantCommissionTest(testModel);
      if (startResponse.code !== 0) {
        message.error(startResponse.message || '启动测试失败');
        setTestResult('failed');
        setTesting(false);
        return;
      }

      const { test_id } = startResponse.data;
      const totalSteps = startResponse.data.total_steps || 10;

      const pollProgress = async () => {
        try {
          const progressResponse = await adminAPI.getMerchantCommissionTestProgress(test_id);
          if (progressResponse.code !== 0) {
            setTimeout(pollProgress, 500);
            return;
          }

          const progress = progressResponse.data;
          if (progress.steps && progress.steps.length > 0) {
            const newSteps = progress.steps.map((s, index) => ({
              name: s.name || `步骤 ${index + 1}`,
              status: s.passed === true ? 'success' : s.passed === false ? 'error' : 'pending',
              detail: s.message || '',
            }));
            setTestSteps(newSteps);
            
            // 计算进度
            const completedSteps = newSteps.filter(s => s.status !== 'pending').length;
            setTestProgress(Math.round((completedSteps / totalSteps) * 100));
            
            // 找到当前正在执行的步骤
            const currentPending = newSteps.find(s => s.status === 'pending');
            if (currentPending) {
              setCurrentStep(currentPending.name);
            }
          }

          if (progress.status === 'completed') {
            setCurrentStep(null);
            setTestProgress(100);
            setTestResult(progress.passed_steps === progress.total_steps ? 'passed' : 'failed');
            setTesting(false);
            message.success(progress.passed_steps === progress.total_steps ? '商户佣金测试全部通过' : '商户佣金测试部分未通过');
            return;
          }

          setTimeout(pollProgress, 300);
        } catch (error) {
          setTimeout(pollProgress, 500);
        }
      };

      pollProgress();
    } catch (error) {
      message.error('测试执行失败');
      setTestResult('failed');
      setTesting(false);
    }
  };

  const userColumns = [
    { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 80 },
    { title: '邮箱', dataIndex: 'email', key: 'email', ellipsis: true },
    { title: 'Redis余额', dataIndex: 'redis_balance', key: 'redis_balance', render: (val) => `¥${(val || 0).toFixed(4)}` },
    { title: 'MySQL余额', dataIndex: 'mysql_balance', key: 'mysql_balance', render: (val) => `¥${(val || 0).toFixed(4)}` },
    {
      title: '对比结果',
      key: 'comparison_result',
      render: (_, record) => {
        const diff = Math.abs((record.redis_balance || 0) - (record.mysql_balance || 0));
        const isMatch = diff < 0.0001;
        return isMatch ? <Tag color="green">一致</Tag> : <Tag color="red">不一致</Tag>;
      }
    }
  ];

  const affiliateColumns = [
    { title: '商户ID', dataIndex: 'affiliate_id', key: 'affiliate_id', width: 80 },
    { title: 'Redis佣金', dataIndex: 'redis_balance_li', key: 'redis_balance_li', render: (val) => `¥${((val || 0) / 1000000).toFixed(6)}` }, // 厘 -> 元
    { title: 'MySQL待提现', dataIndex: 'mysql_pending_balance', key: 'mysql_pending_balance', render: (val) => `¥${((val || 0) / 1000000).toFixed(6)}` }, // 厘 -> 元
    {
      title: '对比结果',
      key: 'comparison_result',
      render: (_, record) => {
        const diff = Math.abs((record.redis_balance_li || 0) - (record.mysql_pending_balance || 0));
        const isMatch = diff === 0; // 精确一致（都是厘单位）
        return isMatch ? <Tag color="green">一致</Tag> : <Tag color="red">不一致</Tag>;
      }
    }
  ];

  // 计算健康状态颜色
  const getHealthColor = (status) => {
    switch (status) {
      case 'healthy': return '#52c41a';
      case 'warning': return '#faad14';
      case 'critical': return '#ff4d4f';
      default: return '#1890ff';
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 24 }}>
        <SafetyCertificateOutlined style={{ marginRight: 8 }} />
        系统维护
      </Title>

      {/* 顶部状态概览 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title={<><ApiOutlined /> API服务</>}
              value={status.api_enabled ? '运行中' : '已关闭'}
              valueStyle={{ color: status.api_enabled ? '#52c41a' : '#ff4d4f', fontSize: 18 }}
              suffix={<Switch size="small" checked={status.api_enabled} onChange={handleSwitchChange} disabled={loading} />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title={<><DollarOutlined /> 用户余额缓存</>}
              value={status.user_balance_count || 0}
              valueStyle={{ color: '#3f8600', fontSize: 18 }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title={<><TeamOutlined /> 商户佣金缓存</>}
              value={status.affiliate_count || 0}
              valueStyle={{ color: '#1890ff', fontSize: 18 }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title={<><ClockCircleOutlined /> 对账间隔</>}
              value={consumerConfig.reconciliation_interval_minutes || 5}
              suffix="分钟"
              valueStyle={{ color: '#722ed1', fontSize: 18 }}
            />
          </Card>
        </Col>
      </Row>

      {/* 标签页 */}
      <Tabs
        defaultActiveKey="stream"
        type="card"
        size="large"
        items={[
          {
            key: 'sync',
            label: <span><SyncOutlined />数据同步</span>,
            children: (
              <>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Card 
                      title="Redis → MySQL 同步" 
                      extra={
                        <Button type="primary" icon={<SyncOutlined spin={syncing} />} onClick={handleSync} loading={syncing}>
                          立即同步
                        </Button>
                      }
                    >
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Alert
                          message="手动同步"
                          description="将Redis中的所有余额数据立即写入MySQL数据库，用于确保数据一致性。"
                          type="info"
                          showIcon
                        />
                        <Button block icon={<SyncOutlined />} onClick={handleCompare}>
                          查看 Redis 与 MySQL 对比
                        </Button>
                        <Divider style={{ margin: '8px 0' }} />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          推荐操作：维护前先关闭API，再执行同步，确保数据完整
                        </Text>
                      </Space>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card title="定时对账配置">
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Alert
                          message="自动对账"
                          description={`系统每 ${consumerConfig.reconciliation_interval_minutes || 5} 分钟自动对比并同步 Redis 和 MySQL 余额`}
                          type="success"
                          showIcon
                        />
                        <Button icon={<SettingOutlined />} onClick={() => setConfigModalVisible(true)}>
                          修改对账配置
                        </Button>
                      </Space>
                    </Card>
                  </Col>
                </Row>

                <Card title="使用说明" style={{ marginTop: 16 }}>
                  <Alert
                    message={
                      <div>
                        <Text strong>推荐操作流程：</Text>
                      </div>
                    }
                    description={
                      <Row gutter={16}>
                        <Col span={6}>
                          <Tag color="blue">1. 关闭API</Tag>
                          <Text type="secondary"> 暂停所有用户请求</Text>
                        </Col>
                        <Col span={6}>
                          <Tag color="green">2. 强制同步</Tag>
                          <Text type="secondary"> 确保数据一致</Text>
                        </Col>
                        <Col span={6}>
                          <Tag color="orange">3. 服务器维护</Tag>
                          <Text type="secondary"> 执行维护操作</Text>
                        </Col>
                        <Col span={6}>
                          <Tag color="purple">4. 开启API</Tag>
                          <Text type="secondary"> 恢复服务</Text>
                        </Col>
                      </Row>
                    }
                    type="info"
                    showIcon
                  />
                </Card>
              </>
            ),
          },
          {
            key: 'stream',
            label: <span><DatabaseOutlined />消息队列</span>,
            children: (
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <Card 
                    title="Redis Stream 队列状态"
                    extra={<Button icon={<SyncOutlined spin={streamLoading} />} onClick={fetchStreamStatus} disabled={streamLoading}>刷新</Button>}
                  >
                    {streamStatus ? (
                      <Row gutter={16}>
                        {/* 总览指标 */}
                        <Col span={6}>
                          <Card size="small" variant="borderless" style={{ background: '#f0f5ff' }}>
                            <Statistic
                              title="总消息数"
                              value={(streamStatus.stream_length || 0) + (streamStatus.commission_stream_length || 0)}
                              valueStyle={{ fontSize: 24, color: '#1890ff' }}
                              suffix="条"
                            />
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              balance: {streamStatus.stream_length || 0} | commission: {streamStatus.commission_stream_length || 0}
                            </Text>
                          </Card>
                        </Col>
                        <Col span={6}>
                          <Card size="small" variant="borderless" style={{ background: streamStatus.total_pending > 0 ? '#fff2f0' : '#f6ffed' }}>
                            <Statistic
                              title="Pending 消息"
                              value={streamStatus.total_pending || 0}
                              valueStyle={{ fontSize: 24, color: streamStatus.total_pending > 0 ? '#ff4d4f' : '#52c41a' }}
                              suffix="条"
                            />
                          </Card>
                        </Col>
                        <Col span={6}>
                          <Card size="small" variant="borderless" style={{ background: '#f6ffed' }}>
                            <Statistic
                              title="活跃消费者"
                              value={streamStatus.active_consumers || 0}
                              valueStyle={{ fontSize: 24, color: '#52c41a' }}
                              suffix="个"
                            />
                          </Card>
                        </Col>
                        <Col span={6}>
                          <Card size="small" variant="borderless" style={{ background: '#f9f0ff' }}>
                            <Statistic
                              title="健康状态"
                              value={streamStatus.health_status === 'healthy' ? '健康' : streamStatus.health_status === 'warning' ? '警告' : streamStatus.health_status === 'critical' ? '严重' : '正常'}
                              valueStyle={{ fontSize: 20, color: getHealthColor(streamStatus.health_status) }}
                            />
                          </Card>
                        </Col>
                      </Row>
                    ) : (
                      <div style={{ textAlign: 'center', padding: 40 }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16 }}>加载中...</div>
                      </div>
                    )}
                  </Card>
                </Col>

                {/* 消费者组管理 */}
                <Col span={16}>
                  <Card title="消费者组管理" size="small">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Text strong>选择消费者组：</Text>
                        <Select
                          value={streamGroup}
                          onChange={setStreamGroup}
                          style={{ width: 300, marginLeft: 8 }}
                        >
                          {streamStatus?.groups?.map(g => (
                            <Select.Option key={g.name} value={g.name}>
                              {g.name} (Pending: {g.pending}, Consumers: {g.consumers})
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                      <Space wrap>
                        <Button type="primary" icon={<SyncOutlined />} onClick={handleRetryAllPending} loading={processingStream} disabled={!streamStatus?.total_pending}>
                          一键处理全部
                        </Button>
                        <Button icon={<SyncOutlined />} onClick={handleRetryPendingMessages} loading={processingStream} disabled={!streamStatus?.total_pending}>
                          处理选中组
                        </Button>
                        <Button danger icon={<DeleteOutlined />} onClick={handleCleanPendingMessages} loading={processingStream} disabled={!currentGroupPending}>
                          清理 Pending
                        </Button>
                        <Button icon={<HistoryOutlined />} onClick={handleTrimProcessedMessages} loading={processingStream}>
                          清理历史消息
                        </Button>
                      </Space>
                    </Space>
                  </Card>
                </Col>

                {/* 消费者配置 */}
                <Col span={8}>
                  <Card title="消费者配置" size="small">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Text>balance-consumers: </Text>
                        <Tag color="blue">{consumerConfig.balance_consumers}</Tag>
                      </div>
                      <div>
                        <Text>commission-consumers: </Text>
                        <Tag color="green">{consumerConfig.commission_consumers}</Tag>
                      </div>
                      <div>
                        <Text>消息保留: </Text>
                        <Tag color="orange">{(consumerConfig.stream_keep_count || 0).toLocaleString()}</Tag>
                      </div>
                      <Button type="link" icon={<SettingOutlined />} onClick={() => setConfigModalVisible(true)}>
                        修改配置
                      </Button>
                    </Space>
                  </Card>
                </Col>
              </Row>
            ),
          },
          {
            key: 'test',
            label: <span><ExperimentOutlined />上线测试</span>,
            children: (
              <Card>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Alert
                    message="完整业务流程测试"
                    description="一键测试用户注册、充值、消费、佣金计算、多种计费方式等完整流程"
                    type="info"
                    showIcon
                  />
                  <Space>
                    <Input addonBefore="测试模型" value={testModel} onChange={e => setTestModel(e.target.value)} style={{ width: 260 }} placeholder="MiniMax-M2.7" disabled={testing} />
                    <Button type="primary" size="large" icon={<PlayCircleOutlined />} onClick={runOnlineTest} loading={testing} disabled={testing}>
                      {testing ? '测试进行中...' : '基础流程测试'}
                    </Button>
                    <Button type="primary" size="large" icon={<DollarOutlined />} onClick={runMerchantCommissionTest} loading={testing} disabled={testing} style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>
                      {testing ? '测试进行中...' : '商户佣金测试'}
                    </Button>
                    <Button size="large" icon={<DeleteOutlined />} onClick={clearCache} style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' }}>
                      清理缓存
                    </Button>
                  </Space>

                  {/* 测试进度条 */}
                  {testing && (
                    <div style={{ marginTop: 16 }}>
                      <Progress percent={testProgress} status="active" />
                      {currentStep && (
                        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                          <LoadingOutlined /> 正在执行: <Text strong>{currentStep}</Text>
                        </Text>
                      )}
                    </div>
                  )}

                  {/* 测试进度 */}
                  {testSteps.length > 0 && (
                    <div style={{ marginTop: 24 }} ref={testContainerRef}>
                      <Divider>测试进度</Divider>
                      <Timeline
                        items={testSteps.map((item, index) => ({
                          color: item.status === 'success' ? 'green' : item.status === 'error' ? 'red' : 'blue',
                          dot: item.status === 'pending' ? <LoadingOutlined spin /> : null,
                          children: (
                            <div ref={index === testSteps.length - 1 ? lastStepRef : null}>
                              <Text strong={item.status !== 'pending'} style={{ color: item.status === 'pending' ? '#999' : 'inherit' }}>
                                {item.status === 'pending' && <Spin size="small" style={{ marginRight: 8 }} />}
                                {item.name || `步骤 ${index + 1}`}
                                {item.status === 'success' && <CheckCircleOutlined style={{ color: '#52c41a', marginLeft: 8 }} />}
                                {item.status === 'error' && <CloseCircleOutlined style={{ color: '#ff4d4f', marginLeft: 8 }} />}
                              </Text>
                              {item.detail && (
                                <div style={{ fontSize: 12, color: '#666', marginTop: 4, whiteSpace: 'pre-wrap' }}>
                                  {item.detail}
                                </div>
                              )}
                            </div>
                          )
                        }))}
                      />
                    </div>
                  )}

                  {/* 测试结果 */}
                  {testResult && (
                    <Alert
                      message={testResult === 'passed' ? '测试通过' : '测试失败'}
                      description={testResult === 'passed' ? '所有测试项目均已通过，系统可以安全上线。' : '部分测试项目未通过，请检查并修复问题。'}
                      type={testResult === 'passed' ? 'success' : 'error'}
                      showIcon
                      style={{ marginTop: 16 }}
                    />
                  )}
                </Space>
              </Card>
            ),
          },
        ]}
      />

      {/* 余额对比弹窗 */}
      <Modal
        title="Redis与MySQL余额对比"
        open={showComparison}
        onCancel={() => setShowComparison(false)}
        footer={[
          <Button key="close" onClick={() => setShowComparison(false)}>关闭</Button>,
          <Button key="sync" type="primary" icon={<SyncOutlined />} onClick={() => { setShowComparison(false); handleSync(); }}>
            同步不一致的数据
          </Button>
        ]}
        width={800}
      >
        <Divider orientation="left">用户余额对比</Divider>
        <Table columns={userColumns} dataSource={comparison.user_balances} rowKey="user_id" size="small" pagination={{ pageSize: 5 }} scroll={{ x: 600 }} />
        
        <Divider orientation="left">商户佣金对比</Divider>
        <Table columns={affiliateColumns} dataSource={comparison.affiliate_balances} rowKey="affiliate_id" size="small" pagination={{ pageSize: 5 }} scroll={{ x: 500 }} />
      </Modal>

      {/* 消费者配置弹窗 */}
      <Modal
        title={<><SettingOutlined /> 消费者配置</>}
        open={configModalVisible}
        onCancel={() => setConfigModalVisible(false)}
        footer={null}
        width={520}
        destroyOnHidden
      >
        <Alert
          message="支持热更新：修改后立即生效，无需重启服务"
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={configForm} onFinish={handleSaveConsumerConfig} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="余额消费者数量" name="balance_consumers" rules={[{ required: true }]}>
                <InputNumber min={1} max={20} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="佣金消费者数量" name="commission_consumers" rules={[{ required: true }]}>
                <InputNumber min={1} max={20} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          
          <Divider style={{ margin: '12px 0' }} />
          
          <Form.Item label="对账间隔（分钟）" name="reconciliation_interval_minutes" extra="定时对账任务执行间隔，默认5分钟">
            <InputNumber min={1} max={60} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label="消息保留数量" name="stream_keep_count" extra="自动清理时保留的历史消息数量">
            <InputNumber min={100} max={100000} step={100} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label="自动清理间隔（分钟）" name="trim_interval_minutes" extra="默认1440分钟（24小时）">
            <InputNumber min={1} max={10080} step={10} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label="超量触发阈值（条）" name="trim_trigger_count" extra="设为0则只按时间间隔清理">
            <InputNumber min={0} max={100000} step={1000} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setConfigModalVisible(false)}>取消</Button>
              <Button type="primary" htmlType="submit" loading={savingConfig}>保存配置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SystemMaintenance;
